
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader, TrendingUp } from 'lucide-react';
import { Transaction } from '@/types/payment';
import { useToast } from '@/hooks/use-toast';

interface InsightsPanelProps {
  transactions: Transaction[];
}

const InsightsPanel: React.FC<InsightsPanelProps> = ({ transactions }) => {
  const { toast } = useToast();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [insights, setInsights] = useState<string[]>([]);

  const analyzeTrends = () => {
    setIsAnalyzing(true);
    
    // Simulate AI analysis delay
    setTimeout(() => {
      // Generate insights based on transaction data
      const newInsights = generateInsights(transactions);
      setInsights(newInsights);
      setIsAnalyzing(false);
      
      toast({
        title: "Analysis Complete",
        description: "AI has analyzed your transaction data and generated insights.",
      });
    }, 2000);
  };

  const generateInsights = (transactions: Transaction[]) => {
    // Ensure numeric calculations by explicitly converting to numbers
    const totalAmount = Number(transactions.reduce((sum, t) => sum + Number(t.amount), 0));
    const totalFees = Number(transactions.reduce((sum, t) => sum + Number(t.fee), 0));
    const bluetoothCount = Number(transactions.filter(t => t.method === 'bluetooth').length);
    const emailCount = Number(transactions.filter(t => t.method === 'email').length);
    const completedCount = Number(transactions.filter(t => t.status === 'completed').length);
    const failedCount = Number(transactions.filter(t => t.status === 'failed').length);
    
    // Fix typescript issue with accumulator typing in reduce
    interface SenderCount {
      [key: string]: number;
    }
    
    // Sort users by transaction count - fixing the type issue
    const senderCounts = transactions.reduce<SenderCount>((acc, t) => {
      acc[t.senderEmail] = (acc[t.senderEmail] || 0) + 1;
      return acc;
    }, {});
    
    const topSenders = Object.entries(senderCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    
    // Calculate percentages safely by checking for zero denominators
    // Explicitly use Number() to ensure type safety
    const feePercentage: number = totalAmount > 0 
      ? Number(((totalFees / totalAmount) * 100).toFixed(2)) 
      : 0;
    const bluetoothPercentage: number = transactions.length > 0 
      ? Number(((bluetoothCount / transactions.length) * 100).toFixed(2)) 
      : 0;
    const emailPercentage: number = transactions.length > 0 
      ? Number(((emailCount / transactions.length) * 100).toFixed(2)) 
      : 0;
    const successRate: number = transactions.length > 0 
      ? Number(((completedCount / transactions.length) * 100).toFixed(2)) 
      : 0;
    const avgTransactionValue: string = transactions.length > 0 
      ? (totalAmount / transactions.length).toFixed(2) 
      : '0.00';
    
    // Generate AI insights based on data
    return [
      `Total processed: $${totalAmount.toFixed(2)} across ${transactions.length} transactions with $${totalFees.toFixed(2)} in fees.`,
      `Revenue: Transaction fees account for ${feePercentage}% of total transaction volume.`,
      `Payment methods: ${bluetoothCount} Bluetooth payments (${bluetoothPercentage}%) and ${emailCount} Email payments (${emailPercentage}%).`,
      `Success rate: ${successRate}% of transactions completed successfully.`,
      topSenders.length > 0 ? `Top users: ${topSenders.map(([email, count]) => `${email} (${count} transactions)`).join(', ')}` : 'No transaction pattern by user detected yet.',
      `${transactions.length > 10 ? 'Transaction volume is growing steadily.' : 'Transaction volume is still low, consider marketing campaigns.'}`,
      `${failedCount > 0 ? `There are ${failedCount} failed transactions that require attention.` : 'All transactions have been processed successfully.'}`,
      `${transactions.length > 0 ? `Average transaction value: $${avgTransactionValue}` : 'No transaction data available for analysis.'}`
    ];
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          AI Transaction Analysis
        </CardTitle>
        <CardDescription>
          Let our AI analyze your transaction data for insights and patterns
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 mb-4">
          {insights.length > 0 ? (
            insights.map((insight, index) => (
              <div key={index} className="p-4 border rounded-lg bg-muted/50">
                <p>{insight}</p>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p className="mb-4">Click "Analyze Data" to generate AI insights</p>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button 
          onClick={analyzeTrends} 
          disabled={isAnalyzing || transactions.length === 0}
        >
          {isAnalyzing ? (
            <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : "Analyze Data"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default InsightsPanel;
